/**
 * useWidgetSurfacePreferences — [T246.5-FE-HOOKS]
 *
 * High-level ergonomic wrapper that composes the four TanStack Query
 * hooks in `features/dashboard/widgetSurfacePreferencesApi.ts` into the
 * single signature expected by the `/widgets/insights` curation UI
 * (T246.5-FE-PAGE) and the cross-surface consumer
 * (`TransactionInsightsCard`, T246.5-FE-WIRE):
 *
 *     const {
 *       preferences,    // WidgetSurfacePreferenceEntry | undefined
 *       isLoading,      // boolean
 *       toggle,         // (kind: string, enabled: boolean) => Promise<void>
 *       reset,          // () => Promise<void>
 *       isUpdating,     // boolean (true while any write mutation is pending)
 *       error,          // Error | null
 *     } = useWidgetSurfacePreferences(surface);
 *
 * Design notes:
 *
 *   • `toggle` always reflects the LATEST pending write — we expose the
 *     combined "any mutation in flight" flag (`isUpdating`) because the UI
 *     needs to disable the entire curation card while a write is pending
 *     (per Wave 6 AC7: "Reset to defaults" CTA disabled during network).
 *
 *   • `error` is the FIRST non-null mutation error since the hook
 *     mounted. We do not aggregate errors from multiple parallel mutations
 *     because the optimistic flow already rolls back the UI on the first
 *     failure — showing two error toasts is noisy.
 *
 *   • No internal state is duplicated. TanStack Query owns the cache; we
 *     only forward call sites. This means a mount / unmount / remount of
 *     the wrapper has zero double-counting effect on cached GETs.
 *
 *   • `reset()` returns a Promise so callers can `await reset()` before
 *     navigating away. It throws on cache-cold failures (see the
 *     underlying mutation's contract).
 *
 * @module hooks/useWidgetSurfacePreferences
 * @see .omo/plans/T246-PY-insights-graph-revamp-prd.md §13.4 (FE-HOOKS)
 */

import { useCallback, useMemo } from 'react';
import {
  useGetWidgetSurfacePreferences,
  useResetWidgetSurfacePreferences,
  useReplaceWidgetSurfacePreferences,
  useToggleWidgetSurfacePreference,
} from '@/features/dashboard/widgetSurfacePreferencesApi';
import type { WidgetSurfacePreferenceEntry } from '@/lib/widgetSurfacePreferences';

export interface UseWidgetSurfacePreferencesResult {
  preferences: WidgetSurfacePreferenceEntry | undefined;
  isLoading: boolean;
  toggle: (kind: string, enabled: boolean) => Promise<unknown>;
  reset: () => Promise<unknown>;
  isUpdating: boolean;
  error: Error | null;
}

/**
 * High-level hook. Pass a surface name to scope the query to one surface,
 * or omit to fetch the all-surfaces map (the wrapper then returns the
 * first entry — typically `transactions`).
 *
 * The `'all'` mode is exposed primarily for the `InsightsGraphManager`
 * page header (which wants the count across surfaces). Most consumers
 * should pass an explicit surface name.
 */
export function useWidgetSurfacePreferences(
  surface?: string,
): UseWidgetSurfacePreferencesResult {
  const effectiveSurface = surface ?? 'transactions';

  // GET single surface (used for the per-surface UI). When `surface` is
  // undefined the hook falls back to the all-surfaces map; we use the
  // first key as the canonical effective surface (which is what the BE
  // also does for the toggle endpoint).
  const surfaceQuery = useGetWidgetSurfacePreferences(effectiveSurface);

  // Mutations.
  const replaceMutation = useReplaceWidgetSurfacePreferences(effectiveSurface);
  const toggleMutation = useToggleWidgetSurfacePreference();
  const resetMutation = useResetWidgetSurfacePreferences(effectiveSurface);

  // Coerce the GET result to a single `WidgetSurfacePreferenceEntry`. The
  // single-surface branch returns the entry directly; the all-surfaces
  // branch returns the map — we project the first entry because that is
  // the only sensible default when the caller omitted a surface.
  const preferences = useMemo<WidgetSurfacePreferenceEntry | undefined>(() => {
    const data = surfaceQuery.data;
    if (!data) return undefined;
    if ('enabled' in data) {
      return data as WidgetSurfacePreferenceEntry;
    }
    const map = data as Record<string, WidgetSurfacePreferenceEntry>;
    const firstKey = Object.keys(map)[0];
    return firstKey ? map[firstKey] : undefined;
  }, [surfaceQuery.data]);

  const toggle = useCallback(
    (kind: string, enabled: boolean) =>
      toggleMutation.mutateAsync({ kind, enabled }),
    [toggleMutation]
  );

  const reset = useCallback(
    () => resetMutation.reset(),
    [resetMutation]
  );

  // Combined "any write in flight" flag.
  const isUpdating =
    toggleMutation.isPending ||
    replaceMutation.isPending ||
    resetMutation.isUpdating;

  // Aggregate error: most recent non-null from any mutation or the GET.
  const error =
    surfaceQuery.error ??
    toggleMutation.error ??
    replaceMutation.error ??
    resetMutation.error ??
    null;

  return {
    preferences,
    isLoading: surfaceQuery.isLoading,
    toggle,
    reset,
    isUpdating,
    error,
  };
}
